import { useMoney } from '../hooks/useMoney';
import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Select,
  DataTable,
  EmptyState,
  Spinner,
  Banner,
  Divider,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface IntrastatItem {
  order_id: number;
  order_number: string;
  dispatch_date: string;
  reference_period: string;
  item_id: number;
  sku: string;
  product_title: string;
  hs_code: string;
  country_of_origin: string;
  weight_kg: number;
  quantity: number;
  unit_price: number;
  line_value: number;
  total_weight_kg: number;
  partner_country: string;
  partner_country_code: string;
  currency: string;
  nature_of_transaction: string;
  flow: string;
}

interface SummaryItem {
  hs_code: string;
  country_of_origin: string;
  partner_country_code: string;
  order_count: number;
  total_quantity: number;
  total_value: number;
  total_weight_kg: number;
}

interface Totals {
  total_orders: number;
  total_lines: number;
  total_value: number;
  total_weight_kg: number;
  unique_hs_codes: number;
  unique_countries: number;
}

interface EUCountry {
  code: string;
  name: string;
}

interface MissingProduct {
  id: number;
  sku: string;
  title: string;
  vendor: string;
  country_of_origin: string;
  order_count: number;
  total_qty_shipped: number;
}

export default function IntrastatReport() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();

  const [data, setData] = useState<IntrastatItem[]>([]);
  const [summary, setSummary] = useState<SummaryItem[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [missingCount, setMissingCount] = useState(0);
  const [euCountries, setEuCountries] = useState<EUCountry[]>([]);
  const [missingProducts, setMissingProducts] = useState<MissingProduct[]>([]);
  const [showMissing, setShowMissing] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
  });
  const [partnerCountry, setPartnerCountry] = useState('');
  const [viewMode, setViewMode] = useState<'detail' | 'summary'>('summary');

  // Export
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  const loadData = useCallback(async () => {
    let url = `/reports/intrastat?start_date=${startDate}&end_date=${endDate}`;
    if (partnerCountry) url += `&partner_country=${partnerCountry}`;

    const result = await fetchApi<{
      data: IntrastatItem[];
      summary: SummaryItem[];
      totals: Totals;
      missing_hs_codes: number;
      eu_countries: EUCountry[];
    }>(url);

    if (result) {
      setData(result.data || []);
      setSummary(result.summary || []);
      setTotals(result.totals);
      setMissingCount(result.missing_hs_codes || 0);
      setEuCountries(result.eu_countries || []);
    }
  }, [fetchApi, startDate, endDate, partnerCountry]);

  const loadMissingProducts = useCallback(async () => {
    const result = await fetchApi<{ data: MissingProduct[] }>(
      `/reports/intrastat/missing?start_date=${startDate}&end_date=${endDate}`
    );
    if (result) {
      setMissingProducts(result.data || []);
    }
  }, [fetchApi, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (showMissing) {
      loadMissingProducts();
    }
  }, [showMissing, loadMissingProducts]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchApi<{ success: boolean; export_id: number; row_count: number }>(
        '/reports/intrastat/export',
        {
          method: 'POST',
          body: JSON.stringify({
            start_date: startDate,
            end_date: endDate,
            format: exportFormat,
            partner_country: partnerCountry || undefined,
          }),
        }
      );
      if (result?.success && result.export_id) {
        window.open(`/api/reports/exports/${result.export_id}/download`, '_blank');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = useMoney();

  const formatNumber = (value: number, decimals = 2) => {
    return value.toFixed(decimals);
  };

  const countryOptions = [
    { label: 'All EU Countries', value: '' },
    ...euCountries.map((c) => ({ label: c.name, value: c.code })),
  ];

  const periodOptions = [
    { label: 'Current Month', value: 'current' },
    { label: 'Last Month', value: 'last' },
    { label: 'Last 3 Months', value: '3months' },
    { label: 'Custom', value: 'custom' },
  ];

  const handlePeriodChange = (value: string) => {
    const now = new Date();
    if (value === 'current') {
      setStartDate(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]);
      setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]);
    } else if (value === 'last') {
      setStartDate(new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0]);
      setEndDate(new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0]);
    } else if (value === '3months') {
      setStartDate(new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().split('T')[0]);
      setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]);
    }
  };

  // Summary table rows (aggregated by HS code + origin + destination)
  const summaryRows = summary.map((item) => [
    item.hs_code || '-',
    item.country_of_origin || '-',
    item.partner_country_code || '-',
    item.order_count.toString(),
    item.total_quantity.toString(),
    `${formatNumber(item.total_weight_kg)} kg`,
    formatCurrency(item.total_value),
  ]);

  // Detail table rows
  const detailRows = data.map((item) => [
    item.order_number,
    item.dispatch_date,
    item.sku,
    item.hs_code || '-',
    item.country_of_origin || '-',
    item.partner_country_code || '-',
    item.quantity.toString(),
    `${formatNumber(item.total_weight_kg)} kg`,
    formatCurrency(item.line_value),
  ]);

  // Missing products table rows
  const missingRows = missingProducts.map((item) => [
    <Button key={item.id} variant="plain" onClick={() => navigate(`/products/${item.id}`)}>
      {item.sku}
    </Button>,
    <div key={`title-${item.id}`} style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {item.title}
    </div>,
    item.vendor || '-',
    item.country_of_origin || '-',
    item.order_count.toString(),
    item.total_qty_shipped.toString(),
  ]);

  return (
    <Page
      title="Intrastat Report"
      subtitle="EU trade statistics for customs declarations"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      secondaryActions={[
        {
          content: showMissing ? 'Hide Missing HS Codes' : `Show Missing HS Codes (${missingCount})`,
          onAction: () => setShowMissing(!showMissing),
          destructive: missingCount > 0,
        },
      ]}
      primaryAction={{
        content: `Export ${exportFormat.toUpperCase()}`,
        onAction: handleExport,
        loading: exporting,
        disabled: summary.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {missingCount > 0 && (
          <Layout.Section>
            <Banner tone="warning" title="Missing HS Codes">
              <Text as="p" variant="bodyMd">
                {missingCount} products shipped in this period are missing HS codes and will not appear in the Intrastat report.
                Click "Show Missing HS Codes" to view and update them.
              </Text>
            </Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {totals && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Orders</Text>
                  <Text variant="heading2xl" as="p">{totals.total_orders}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{totals.total_lines} line items</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Total Value</Text>
                  <Text variant="heading2xl" as="p">{formatCurrency(totals.total_value)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Invoice value</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Net Mass</Text>
                  <Text variant="heading2xl" as="p">{formatNumber(totals.total_weight_kg)} kg</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Total weight</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Commodity Codes</Text>
                  <Text variant="heading2xl" as="p">{totals.unique_hs_codes}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{totals.unique_countries} partner countries</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Filters</Text>
              <InlineStack gap="400" align="start">
                <div style={{ width: 150 }}>
                  <Select
                    label="Period"
                    options={periodOptions}
                    value="custom"
                    onChange={handlePeriodChange}
                  />
                </div>
                <div style={{ width: 150 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', width: '100%' }}
                  />
                </div>
                <div style={{ width: 150 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', width: '100%' }}
                  />
                </div>
                <div style={{ width: 180 }}>
                  <Select
                    label="Partner Country"
                    options={countryOptions}
                    value={partnerCountry}
                    onChange={setPartnerCountry}
                  />
                </div>
                <div style={{ width: 120 }}>
                  <Select
                    label="Export Format"
                    options={[
                      { label: 'CSV', value: 'csv' },
                      { label: 'Intrastat', value: 'intrastat' },
                    ]}
                    value={exportFormat}
                    onChange={setExportFormat}
                  />
                </div>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Missing Products Section */}
        {showMissing && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="headingMd" as="h2">Products Missing HS Codes</Text>
                  <Badge tone="warning">{`${missingProducts.length} products`}</Badge>
                </InlineStack>
                {missingProducts.length === 0 ? (
                  <Text as="p" tone="subdued">No products missing HS codes in this period.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric']}
                    headings={['SKU', 'Product', 'Vendor', 'Origin', 'Orders', 'Qty Shipped']}
                    rows={missingRows}
                  />
                )}
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* View Mode Toggle */}
        <Layout.Section>
          <InlineStack gap="200">
            <Button
              pressed={viewMode === 'summary'}
              onClick={() => setViewMode('summary')}
            >
              Summary View
            </Button>
            <Button
              pressed={viewMode === 'detail'}
              onClick={() => setViewMode('detail')}
            >
              Detail View
            </Button>
          </InlineStack>
        </Layout.Section>

        {/* Data Table */}
        <Layout.Section>
          <Card>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            ) : viewMode === 'summary' ? (
              summary.length === 0 ? (
                <EmptyState
                  heading="No Intrastat data"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>No fulfilled orders with HS codes found for this period.</p>
                </EmptyState>
              ) : (
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">Aggregated by Commodity Code</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Data aggregated by HS code, country of origin, and partner member state - ready for Intrastat submission.
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric', 'text', 'numeric']}
                    headings={['CN8 Code', 'Origin', 'Partner', 'Orders', 'Quantity', 'Net Mass', 'Value']}
                    rows={summaryRows}
                    footerContent={`${summary.length} commodity code combinations`}
                  />
                </BlockStack>
              )
            ) : (
              data.length === 0 ? (
                <EmptyState
                  heading="No Intrastat data"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>No fulfilled orders with HS codes found for this period.</p>
                </EmptyState>
              ) : (
                <BlockStack gap="400">
                  <Text variant="headingMd" as="h2">Order Line Details</Text>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'numeric']}
                    headings={['Order', 'Date', 'SKU', 'HS Code', 'Origin', 'Partner', 'Qty', 'Weight', 'Value']}
                    rows={detailRows}
                    footerContent={`${data.length} line items`}
                  />
                </BlockStack>
              )
            )}
          </Card>
        </Layout.Section>

        {/* Info Section */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">About Intrastat Reporting</Text>
              <Text as="p" variant="bodyMd">
                Intrastat is the system for collecting statistics on the trade in goods between EU member states.
                Businesses must submit declarations if they exceed the reporting thresholds set by their member state.
              </Text>
              <Divider />
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Required Data Fields</Text>
                <InlineStack gap="400" wrap>
                  <Badge>CN8 Commodity Code</Badge>
                  <Badge>Partner Member State</Badge>
                  <Badge>Country of Origin</Badge>
                  <Badge>Net Mass (kg)</Badge>
                  <Badge>Invoice Value</Badge>
                  <Badge>Nature of Transaction</Badge>
                </InlineStack>
              </BlockStack>
              <Divider />
              <Text as="p" variant="bodySm" tone="subdued">
                HS codes and country of origin are synced from Shopify product data.
                Ensure your products have these fields set in Shopify to appear in this report.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
